/**
 * Traces WebViewer render activity across both viewers into the debug panel's buffer.
 *
 * Output goes to the panel only — no console echo and no stack traces, because logging thousands of
 * events to the console is itself slow enough to change the render timings being investigated.
 */
import { WebViewerInstance } from '@pdftron/webviewer';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import { DocumentTypes } from 'types';
import { RenderLogDoc } from './renderLogConstants';
import { appendRenderLogEntry } from './renderLogBuffer';

/**
 * documentViewer events to trace, with a formatter that keeps only the useful bits of each event's
 * payload (e.g. page numbers rather than canvas nodes).
 */
const RENDER_EVENTS: Array<{ event: string; format: (...args: unknown[]) => unknown[] }> = [
  { event: 'beginRendering', format: () => [] },
  { event: 'finishedRendering', format: () => [] },
  { event: 'pageComplete', format: (pageNum) => [`page ${pageNum}`] },
  { event: 'pagesUpdated', format: (changes) => [changes] },
  { event: 'visiblePagesChanged', format: (pages) => [`pages ${JSON.stringify(pages)}`] },
  { event: 'pageNumberUpdated', format: (pageNum) => [`current page ${pageNum}`] },
  { event: 'zoomUpdated', format: (zoom) => [`zoom ${zoom}`] },
  { event: 'displayPageLocation', format: (pageNum) => [`page ${pageNum}`] },
];

const WRAPPED_METHODS = ['refreshAll', 'updateView'] as const;

const DOCUMENT_TYPES = [DocumentTypes.source, DocumentTypes.target] as const;

/** documentViewer's typed addEventListener only accepts known event names, so widen it. */
type LooseEventTarget = {
  addEventListener: (event: string, cb: (...args: unknown[]) => void) => void;
  removeEventListener: (event: string, cb: (...args: unknown[]) => void) => void;
};

type PatchableViewer = Record<string, (...args: unknown[]) => unknown>;

interface HookedViewer {
  instance: WebViewerInstance;
  cleanups: Array<() => void>;
}

const hookedViewers = new Map<DocumentTypes, HookedViewer>();

function getDocTag(documentType: DocumentTypes): RenderLogDoc {
  return documentType === DocumentTypes.source ? 'SOURCE' : 'TARGET';
}

function formatDetail(args: unknown[]): string {
  if (args.length === 0) return '';
  return args
    .map((arg) => {
      if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') {
        return String(arg);
      }
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');
}

function hookViewer(documentType: DocumentTypes, instance: WebViewerInstance) {
  const documentViewer = instance.Core.documentViewer;
  const tag = getDocTag(documentType);
  const cleanups: Array<() => void> = [];

  const looseViewer = documentViewer as unknown as LooseEventTarget;
  RENDER_EVENTS.forEach(({ event, format }) => {
    const handler = (...args: unknown[]) => appendRenderLogEntry(tag, event, formatDetail(format(...args)));
    looseViewer.addEventListener(event, handler);
    cleanups.push(() => looseViewer.removeEventListener(event, handler));
  });

  // refreshAll/updateView are the expensive calls we care about, and they fire no events — the only
  // way to see them is to wrap them. Originals are restored when the viewer is unhooked.
  const patchable = documentViewer as unknown as PatchableViewer;
  WRAPPED_METHODS.forEach((method) => {
    const original = patchable[method];
    if (typeof original !== 'function') return;

    const bound = original.bind(documentViewer);
    patchable[method] = (...args: unknown[]) => {
      appendRenderLogEntry(tag, `${method}() called`);
      return bound(...args);
    };
    cleanups.push(() => {
      patchable[method] = original;
    });
  });

  hookedViewers.set(documentType, { instance, cleanups });
}

function unhookViewer(documentType: DocumentTypes) {
  const hooked = hookedViewers.get(documentType);
  if (!hooked) return;

  // Reverse order so wrapped methods are restored before the listeners that were added first.
  [...hooked.cleanups].reverse().forEach((cleanup) => cleanup());
  hookedViewers.delete(documentType);
}

/**
 * Brings the hooks in line with the viewers that currently exist. Safe to call repeatedly: a viewer
 * already hooked to the same instance is left alone, a replaced instance is re-hooked, and a viewer
 * that has gone away is unhooked.
 */
export function syncRenderLogging() {
  DOCUMENT_TYPES.forEach((documentType) => {
    const instance = PDFManagerFactory.getViewer(documentType)?.instance;

    if (!instance) {
      unhookViewer(documentType);
      return;
    }
    if (hookedViewers.get(documentType)?.instance === instance) return;

    unhookViewer(documentType);
    hookViewer(documentType, instance);
  });
}

export function stopRenderLogging() {
  DOCUMENT_TYPES.forEach(unhookViewer);
}

/** @internal Exposed for tests — the number of hooks currently installed across both viewers. */
export function getRenderLogHookCount(): number {
  let count = 0;
  hookedViewers.forEach(({ cleanups }) => {
    count += cleanups.length;
  });
  return count;
}
